const HEIC_EXTENSIONS = ['.heic', '.heif'];

const TYPE_PARAM = 'type';
const JPEG = 'jpeg';

/**
 * Extension is checked on the path alone — the query and hash are cut off
 * first. A signed upload URL carries its own query string, so testing the whole
 * string would both miss `…/photo.heic?sig=…` and false-positive on
 * `…/photo.png?name=x.heic`.
 */
const isHeicUrl = (url: string): boolean => {
  const [path] = url.toLowerCase().split(/[?#]/);
  return HEIC_EXTENSIONS.some((extension) => path.endsWith(extension));
};

const hasTypeParam = (url: string): boolean => {
  const [, query] = url.split('?');
  if (!query) return false;
  return new URLSearchParams(query).has(TYPE_PARAM);
};

/**
 * Asks the CDN for a JPEG when the image being uploaded is HEIC.
 *
 * HEIC is what iOS hands over from the camera roll, and the flow backends do
 * not decode it — so the URL is passed through the same `type=` transform the
 * CDN already uses for thumbnails (see `optimizeImageUrl`, which asks for
 * `type=webp`) and the flow receives a JPEG instead.
 *
 * Left untouched when the URL already carries a `type` param: something
 * upstream has already stated the format it wants, and a second `type=` would
 * be ambiguous. That also makes this idempotent — applying it twice is a no-op,
 * so a second call further down the pipeline is harmless.
 *
 * Returns the input unchanged for every non-HEIC URL, so it is safe to apply to
 * any media URL. Takes a plain `string`: callers holding a nullable url (input
 * `src` is `string | null | undefined`) guard first, rather than this quietly
 * passing nullish values through.
 */
const withJpegConversion = (url: string): string => {
  if (!isHeicUrl(url) || hasTypeParam(url)) return url;

  // Split the fragment off first: appending after it would put the param
  // inside the hash, where it is not a query param at all.
  const hashIndex = url.indexOf('#');
  const addressable = hashIndex === -1 ? url : url.slice(0, hashIndex);
  const hash = hashIndex === -1 ? '' : url.slice(hashIndex);

  const separator = addressable.includes('?') ? '&' : '?';
  return `${addressable}${separator}${TYPE_PARAM}=${JPEG}${hash}`;
};

export default withJpegConversion;
